// pages/priority.js — Priority page.
//
// Owns the `data-page="priority"` section. Renders the priority rule
// list (drag-drop reorderable, filter by text + decision chips).
// Each rule defines preempt behavior for collisions between
// task-priorities. Cloud and local modes use the same /v1/priority
// endpoint.
//
// Migration note: only top-level renderPriorityPage moves here.
// Sub-renderers (applyPriorityListFilter, renderPriorityFilterBar,
// renderPriorityRuleRow, wirePriorityRuleDragDrop, fetchPriority)
// stay in dashboard.js, called via legacy bridge.
//
// Per Worktrees bug-sweep finding: no initial render in start() —
// legacy code handles activePage-change render.
import { state } from "../state.js";
import { priorityState } from "../page-state.js";
import { patchHTML } from "../dom.js";
import { escapeHTML } from "../util.js";
import { fetchPriority } from "../fetchers.js";
import { isLocalDaemonMode } from "../ui.js";
import {
  applyPriorityListFilter,
  renderPriorityFilterBar,
  renderPriorityRuleRow,
  wirePriorityRuleDragDrop,
  setPriorityReorderCallback,
} from "../priority.js";

// Register the post-reorder render callback. Mirrors the policy
// page's pattern — avoids circular import between pages/priority.js
// and priority.js.
setPriorityReorderCallback(() => renderPriorityPage());

export async function renderPriorityPage() {

  if (!priorityState.data && !priorityState.error) await fetchPriority();
  const body = document.getElementById("priority-body");
  if (!body) return;
  if (priorityState.error) {
    patchHTML(
      body,
      '<div class="empty"><div class="empty-title">Could not load priority ruleset</div><div class="empty-detail">' +
        escapeHTML(priorityState.error) +
        "</div></div>",
    );
    return;
  }
  const p = priorityState.data;
  if (!p) return;

  // The daemon has no endpoints to edit priority rules; they are edited on
  // the website, where they apply to every computer on the team.
  const readOnly = isLocalDaemonMode();
  const createBtn = document.getElementById("priority-create-btn");
  if (createBtn) createBtn.style.display = readOnly ? "none" : "";

  let html = "";
  if (readOnly) {
    html +=
      '<div class="card" style="padding: 14px 16px; margin-bottom: 16px; display: flex; align-items: center; gap: 12px; flex-wrap: wrap;">' +
      '<div style="flex: 1; min-width: 220px; font-size: 13px; color: var(--text-secondary);">Priority rules are edited on the website, where they apply to all your computers. Rules only for this computer can go in <code>~/.agentcollision/priority.toml</code>; press Reload after editing it.</div>' +
      '<a class="btn btn-sm btn-primary" href="https://dashboard.agentcollision.com/priority" target="_blank" rel="noopener">Edit on the website</a>' +
      "</div>";
  }

  // Quiet default-priority summary line, only when set.
  if (p.default_priority) {
    html +=
      '<div style="margin-bottom: 16px; font-size: 13px; color: var(--text-secondary);">';
    html +=
      'Default priority: <code style="font-size: 12px; padding: 1px 6px; background: var(--bg-elevated); border-radius: 3px;">' +
      escapeHTML(p.default_priority) +
      "</code>";
    if (p.priorities?.length) {
      html +=
        "  ·  Levels: " +
        p.priorities
          .map(
            (x) =>
              '<code style="font-size: 12px; padding: 1px 6px; background: var(--bg-elevated); border-radius: 3px;">' +
              escapeHTML(x) +
              "</code>",
          )
          .join(" ");
    }
    html += "</div>";
  }

  if (!p.rules || p.rules.length === 0) {
    html +=
      '<div class="card" style="padding: 32px; text-align: center; color: var(--text-secondary);">No priority rules yet.' +
      (readOnly ? "" : " Click <strong>+ Create rule</strong> above to add one.") +
      "</div>";
  } else {
    const filtered = applyPriorityListFilter(p.rules);
    html += renderPriorityFilterBar(p.rules, filtered);
    html +=
      '<div style="font-size: 12px; color: var(--text-secondary); margin-bottom: 10px; display: flex; align-items: center; gap: 6px;">' +
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="opacity: 0.7;"><polyline points="6 9 12 15 18 9"/></svg>' +
      "<span><strong>Evaluated in order, first match wins.</strong>" +
      (readOnly ? "" : ' Drag the <span style="opacity: 0.6;">⋮⋮</span> handle to reorder.') +
      "</span>" +
      "</div>";
    if (filtered.length === 0) {
      html +=
        '<div class="card" style="padding: 24px; text-align: center; color: var(--text-secondary); font-size: 13px;">No rules match the current filter. <button class="btn-pick" type="button" id="priority-filter-clear" style="margin-left: 8px;">Clear filter</button></div>';
    } else {
      html +=
        '<div class="priority-rule-list" style="border: 1px solid var(--border); border-radius: var(--r-md); overflow: hidden;">';
      for (const r of filtered)
        html += renderPriorityRuleRow(r, { readOnly, label: "Rule " + (p.rules.indexOf(r) + 1) });
      html += "</div>";
    }
  }

  patchHTML(body, html);
  if (!readOnly) wirePriorityRuleDragDrop();
}

// ---- mount ----

let started = false;
let tickHandler = null;

function start() {
  if (started) return;
  started = true;
  // Initial render — cloud mode has no daemon-driven tick loop, so
  // without this the page sits on its skeleton until manual nav.
  // See policy.js / worktrees.js for the same fix.
  renderPriorityPage();
  tickHandler = () => {
    if (state.activePage === "priority") renderPriorityPage();
  };
  document.addEventListener("dashboard:tick", tickHandler);
}

function stop() {
  if (!started) return;
  started = false;
  if (tickHandler) {
    document.removeEventListener("dashboard:tick", tickHandler);
    tickHandler = null;
  }
}

function bindLifecycle() {
  const section = document.querySelector('section.page[data-page="priority"]');
  if (!section) return;
  if (section.classList.contains("active")) start();
  const obs = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName !== "class") return;
      if (section.classList.contains("active")) start();
      else stop();
    });
  });
  obs.observe(section, { attributes: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}
